import React, { useCallback, useMemo } from 'react';
import { FormPreset } from '@form-engine/core-application/types/form';
import { i18n } from '@form-engine/builder-application';
import useFormPresetApi, {
    refreshFormPresets,
} from '@form-engine/builder-application/hooks/use-form-preset-api';
import {
    BuilderConfirmModal,
    BuilderModalProps,
} from '@form-engine/builder-application/components/modal';
import { StoreMaskProvider } from '@form-engine/builder-mask/hooks/use-mask';
import dialog, {
    confirm,
} from '@form-engine/builder-application/components/dialog';
import BuilderFormPresetMask from './mask';
import { useStore } from '@form-engine/core-application/hooks/use-store';
import { Button } from 'react-bootstrap';
import { Trash2Fill } from 'react-bootstrap-icons';
import schema from './schema';

export function BuilderFormPresetDialog({
    preset,
    onSuccess,
    onError,
    ...props
}: BuilderModalProps & {
    preset: FormPreset;
    onSuccess?: (result: FormPreset) => void;
    onError?: (error: Error) => void;
}) {
    const presetStore = useStore(preset);
    const presetApi = useFormPresetApi();
    const schemaError = useMemo(() => {
        const parseResult = schema.safeParse(presetStore?.state);
        if (parseResult.success) return null;
        return parseResult.error;
    }, [presetStore?.state]);

    const handleSubmit = useCallback(async () => {
        if (schemaError) return;

        if (!presetStore?.state?.id) {
            onError?.(new Error('tried to save invalid form preset'));
            return;
        }

        try {
            const result = await presetApi.update(presetStore.state);
            refreshFormPresets();
            onSuccess?.(result);
        } catch (e) {
            onError?.(e as Error);
        }
    }, [presetStore?.state, presetApi, onSuccess, onError, schemaError]);

    const handleDelete = useCallback(async () => {
        if (!preset?.id) return;
        if (
            await confirm({
                children: i18n.c('form-editor.savePresetDialog.deleteMessage'),
            })
        ) {
            try {
                await presetApi.delete(preset.id);
                refreshFormPresets();
                onSuccess?.(preset);
            } catch (e) {
                onError?.(e as Error);
            }
        }
    }, [preset?.id, presetApi, onSuccess, onError]);

    const deleteButton = (
        <Button
            size="sm"
            variant="danger"
            className="me-auto"
            onClick={handleDelete}
        >
            <Trash2Fill /> {i18n.c('form-editor.savePresetDialog.deleteButton')}
        </Button>
    );

    if (!presetStore) return null;

    return (
        <BuilderConfirmModal
            {...props}
            prependFooter={deleteButton}
            size="lg"
            onConfirm={handleSubmit}
            disabled={Boolean(schemaError)}
            title={i18n.c('form-editor.savePresetDialog.title')}
        >
            <StoreMaskProvider store={presetStore}>
                <BuilderFormPresetMask />
            </StoreMaskProvider>
        </BuilderConfirmModal>
    );
}

async function openFormPresetDialog(preset: FormPreset) {
    try {
        return (await dialog({
            as: BuilderFormPresetDialog,
            preset,
        })) as FormPreset;
    } catch (e) {
        console.error(e);
        return preset;
    }
}

export function useFormPresetDialog() {
    return useCallback(openFormPresetDialog, []);
}
